import {
  ENGLISH_HELPERS,
  PREFIXES,
  SUFFIXES,
} from "@/lib/tool-kit/word-knowledge/roots-data-affixes";
import { GREEK_ROOTS } from "@/lib/tool-kit/word-knowledge/roots-data-greek";
import { LATIN_ROOTS } from "@/lib/tool-kit/word-knowledge/roots-data-latin";

/**
 * Greek and Latin roots, prefixes and suffixes: a searchable list and a word breaker.
 *
 * The entries are our own — meanings and example words written for this tool, with the
 * source word and sense checked against Wiktionary and Etymonline — in the compact line
 * format described in `roots-data-greek.ts`. Nothing is copied from a published root
 * list or dictionary.
 *
 * The word breaker is a pattern matcher, not an etymologist. It finds the cheapest way
 * to cover a word with known parts, where a prefix may only open the word, a suffix may
 * only close it, short forms cost more than long ones and an unexplained letter costs
 * the most. So it explains *biography* as bio + graphy and *transportation* as trans +
 * port + ation, and it says plainly when it cannot read a word. Where a spelling
 * belongs to more than one entry (ped- is both "child" and "foot") it names the others.
 */

export type PartKind = "root" | "prefix" | "suffix";
export type Origin = "greek" | "latin" | "both" | "english";

export interface RootEntry {
  id: string;
  kind: PartKind;
  /** Spellings the part takes in English; the first is the one shown. */
  forms: string[];
  origin: Origin;
  /** The Greek or Latin word it comes from; empty for English helpers. */
  source: string;
  meaning: string;
  examples: string[];
  /** English prefixes and endings used only by the word breaker. */
  helper: boolean;
}

const KINDS: Readonly<Record<string, PartKind>> = {
  r: "root",
  p: "prefix",
  s: "suffix",
};

const ORIGINS: Readonly<Record<string, Origin>> = {
  G: "greek",
  L: "latin",
  GL: "both",
  E: "english",
};

const RE_LINE_BREAK = /\n/;
const RE_COMMA = /,\s*/;

/** The compact text → entries. Throws on a malformed line, so a bad edit fails a test. */
export function parseRootEntries(text: string, prefix: string): RootEntry[] {
  const entries: RootEntry[] = [];
  for (const [index, line] of text.split(RE_LINE_BREAK).entries()) {
    if (!line.trim()) {
      continue;
    }
    const [kind, forms, origin, source, meaning, examples] = line.split("|");
    const parsedKind = KINDS[kind];
    const parsedOrigin = ORIGINS[origin];
    if (!(parsedKind && parsedOrigin && forms && meaning && examples)) {
      throw new Error(`Malformed root entry: ${line}`);
    }
    entries.push({
      id: `${prefix}${index}`,
      kind: parsedKind,
      forms: forms.split(","),
      origin: parsedOrigin,
      source: source ?? "",
      meaning,
      examples: examples.split(RE_COMMA),
      helper: parsedOrigin === "english",
    });
  }
  return entries;
}

const KIND_ORDER: Readonly<Record<PartKind, number>> = {
  root: 0,
  prefix: 1,
  suffix: 2,
};

/** Every listed entry, roots first (Greek, then Latin), then prefixes, then suffixes. */
export const ROOT_ENTRIES: readonly RootEntry[] = [
  ...parseRootEntries(GREEK_ROOTS, "g"),
  ...parseRootEntries(LATIN_ROOTS, "l"),
  ...parseRootEntries(PREFIXES, "p"),
  ...parseRootEntries(SUFFIXES, "s"),
];

const HELPER_ENTRIES: readonly RootEntry[] = parseRootEntries(
  ENGLISH_HELPERS,
  "e"
);

/** The form as printed: `graph`, `pre-`, `-able`. */
export function displayForm(entry: RootEntry, form = entry.forms[0]): string {
  if (entry.kind === "prefix") {
    return `${form}-`;
  }
  return entry.kind === "suffix" ? `-${form}` : form;
}

/* ------------------------------------------------------------------- search */

export type KindFilter = "all" | PartKind;
export type OriginFilter = "all" | "greek" | "latin";

export interface RootSearch {
  query: string;
  kind: KindFilter;
  origin: OriginFilter;
}

const RE_EDGE_HYPHENS = /^-+|-+$/g;
const RE_SPACES = /\s+/;
const RE_NON_LETTER = /[^\p{L}'-]/gu;
const RE_NON_MEANING_LETTER = /[^a-z]+/;
const RE_SILENT_E = /e$/;

export function normalizeRootQuery(input: string): string {
  return input.trim().toLowerCase().replace(RE_EDGE_HYPHENS, "");
}

function matchesFilter(entry: RootEntry, search: RootSearch): boolean {
  if (search.kind !== "all" && entry.kind !== search.kind) {
    return false;
  }
  if (search.origin === "all") {
    return true;
  }
  return entry.origin === "both" || entry.origin === search.origin;
}

/** How well one query word matches an entry; 0 means not at all. */
function tokenScore(entry: RootEntry, token: string): number {
  let best = 0;
  for (const form of entry.forms) {
    if (form === token) {
      best = Math.max(best, 100);
    } else if (form.startsWith(token)) {
      best = Math.max(best, 70);
    }
  }
  const words = entry.meaning.toLowerCase().split(RE_NON_MEANING_LETTER);
  if (words.includes(token)) {
    best = Math.max(best, 55);
  } else if (token.length >= 3 && entry.meaning.toLowerCase().includes(token)) {
    best = Math.max(best, 35);
  }
  for (const example of entry.examples) {
    const word = example.toLowerCase();
    if (word === token) {
      best = Math.max(best, 60);
    } else if (token.length >= 3 && word.includes(token)) {
      best = Math.max(best, 30);
    }
  }
  if (token.length >= 3 && entry.source.toLowerCase().includes(token)) {
    best = Math.max(best, 25);
  }
  return best;
}

/**
 * Entries matching the filters and the query, best match first. An empty query lists
 * every entry in the filters, roots then prefixes then suffixes, alphabetically. Several
 * words in the query must all match; an exact form (`dict`) ranks above a meaning
 * (`say`) above an example word (`predict`).
 */
export function searchRoots(search: RootSearch): RootEntry[] {
  const query = normalizeRootQuery(search.query);
  const tokens = query.split(RE_SPACES).filter(Boolean);
  const pool = ROOT_ENTRIES.filter((entry) => matchesFilter(entry, search));
  if (tokens.length === 0) {
    return [...pool].sort(
      (a, b) =>
        KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
        a.forms[0].localeCompare(b.forms[0], "en")
    );
  }
  const scored: { entry: RootEntry; score: number }[] = [];
  for (const entry of pool) {
    let total = 0;
    let every = true;
    for (const token of tokens) {
      const score = tokenScore(entry, token);
      if (score === 0) {
        every = false;
        break;
      }
      total += score;
    }
    if (every) {
      scored.push({ entry, score: total });
    }
  }
  return scored
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.entry.forms[0].localeCompare(b.entry.forms[0], "en")
    )
    .map((item) => item.entry);
}

/* ---------------------------------------------------------------- the breaker */

export interface WordPart {
  /** The letters of the word this part covers, as typed. */
  text: string;
  kind: PartKind | "glue" | "unknown";
  /** The spelling of the entry that matched (`tion`, where the entry is headed `ation`). */
  form: string | null;
  entry: RootEntry | null;
  /** Other entries with the same spelling and kind: check which sense fits. */
  alternatives: RootEntry[];
}

export interface Breakdown {
  word: string;
  parts: WordPart[];
  /** Share of the letters explained by a part or a joining vowel, 0–1. */
  coverage: number;
}

export type WordAnalysis =
  | { status: "parts"; breakdown: Breakdown }
  | { status: "lookalike"; word: string }
  | { status: "none"; word: string }
  | { status: "invalid" };

/**
 * Everyday words that contain a Greek or Latin spelling by accident: *string* is not
 * built on stringere, *another* has no ad- and no haerere. The breaker would read them
 * as parts, so it declines.
 */
const LOOKALIKES: ReadonlySet<string> = new Set(
  "string strings another other others together understand whether weather mother father brother either neither rather feather leather gather bother thousand hundred children married ordinary".split(
    " "
  )
);

interface FormMatch {
  entry: RootEntry;
  form: string;
}

type FormIndex = Map<string, FormMatch[]>;

function buildFormIndex(kind: PartKind, helpers: boolean): FormIndex {
  const index: FormIndex = new Map();
  const source = helpers ? HELPER_ENTRIES : ROOT_ENTRIES;
  for (const entry of source) {
    if (entry.kind !== kind) {
      continue;
    }
    for (const form of entry.forms) {
      const list = index.get(form) ?? [];
      list.push({ entry, form });
      index.set(form, list);
    }
  }
  return index;
}

function mergeIndexes(a: FormIndex, b: FormIndex): FormIndex {
  const merged: FormIndex = new Map(a);
  for (const [form, matches] of b) {
    merged.set(form, [...(merged.get(form) ?? []), ...matches]);
  }
  return merged;
}

const PREFIX_INDEX = mergeIndexes(
  buildFormIndex("prefix", false),
  buildFormIndex("prefix", true)
);
const ROOT_INDEX = buildFormIndex("root", false);
const SUFFIX_INDEX = mergeIndexes(
  buildFormIndex("suffix", false),
  buildFormIndex("suffix", true)
);

const MAX_FORM = 12;
const MAX_WORD = 40;
const MIN_WORD = 4;
const UNKNOWN_COST = 3;
const PART_COST = 1;
const GLUE_COST = 0.3;
const SILENT_E_COST = 0.2;
const MID_PREFIX_COST = 1.5;
const SUFFIX_BONUS = 0.3;
const OPENING_PREFIX_BONUS = 0.5;
const ROOT_LENGTH_BONUS = 0.1;
const LONG_FORM = 4;
const MIN_COVERAGE = 0.5;
const MIN_KNOWN = 4;

function shortPenalty(form: string, helper: boolean): number {
  if (helper) {
    return 0.5;
  }
  if (form.length >= LONG_FORM) {
    return 0;
  }
  return form.length === 3 ? 1 : 3;
}

type State = "start" | "prefix" | "root" | "stem" | "suffix";

interface Piece {
  start: number;
  end: number;
  kind: WordPart["kind"];
  matches: FormMatch[];
}

interface Step {
  from: number;
  fromState: State;
  cost: number;
  part: Piece | null;
}

type Table = Map<string, Step>;

function key(position: number, state: State): string {
  return `${position}:${state}`;
}

interface Candidate {
  form: string;
  matches: FormMatch[];
}

function candidates(
  index: FormIndex,
  word: string,
  start: number
): Candidate[] {
  const found: Candidate[] = [];
  const limit = Math.min(MAX_FORM, word.length - start);
  for (let length = 1; length <= limit; length += 1) {
    const form = word.slice(start, start + length);
    const matches = index.get(form);
    if (matches) {
      found.push({ form, matches });
    }
  }
  return found;
}

/** Is there a known root or suffix of at least three letters starting here? */
function longAt(word: string, position: number, indexes: FormIndex[]): boolean {
  return indexes.some((index) =>
    candidates(index, word, position).some((item) => item.form.length >= 3)
  );
}

function startsRoot(word: string, position: number): boolean {
  return longAt(word, position, [ROOT_INDEX]);
}

function startsSuffix(word: string, position: number): boolean {
  return (
    position >= word.length ||
    candidates(SUFFIX_INDEX, word, position).length > 0
  );
}

/** Offer `step` for the state at `position` if it is cheaper than what is there. */
function relax(table: Table, position: number, state: State, step: Step): void {
  const current = table.get(key(position, state));
  if (!current || step.cost < current.cost) {
    table.set(key(position, state), step);
  }
}

type Mover = (
  end: number,
  next: State,
  cost: number,
  kind: WordPart["kind"],
  matches?: FormMatch[]
) => void;

interface Context {
  word: string;
  position: number;
  state: State;
  move: Mover;
}

function isHelper(matches: FormMatch[]): boolean {
  return matches.every((match) => match.entry.helper);
}

/** A prefix then a root: the "de" and "grad" after "bio" in biodegradable. */
function prefixThenRoot(word: string, position: number): boolean {
  return candidates(PREFIX_INDEX, word, position).some((item) =>
    startsRoot(word, position + item.form.length)
  );
}

function movePrefixes({ word, position, state, move }: Context): void {
  const middle = state !== "start" && state !== "prefix";
  for (const { form, matches } of candidates(PREFIX_INDEX, word, position)) {
    const end = position + form.length;
    const helper = isHelper(matches);
    const anchored =
      form.length >= LONG_FORM ||
      helper ||
      longAt(word, end, [ROOT_INDEX, PREFIX_INDEX]) ||
      startsKnownSuffix(word, end);
    if (end >= word.length || !anchored) {
      continue;
    }
    // After another prefix only a longer prefix chains, so "ar" + "ac" explains nothing.
    if (state === "prefix" && form.length < 3 && !helper) {
      continue;
    }
    // In the middle of a word a prefix needs a root straight after it (bio + de + grad).
    if (middle && !startsRoot(word, end)) {
      continue;
    }
    let cost = PART_COST + shortPenalty(form, helper);
    if (middle) {
      cost += MID_PREFIX_COST;
    } else if (position === 0) {
      cost -= OPENING_PREFIX_BONUS;
    }
    move(end, "prefix", cost, "prefix", matches);
  }
}

function moveRoots({ word, position, state, move }: Context): void {
  for (const { form, matches } of candidates(ROOT_INDEX, word, position)) {
    const end = position + form.length;
    const anchored =
      form.length >= LONG_FORM ||
      state === "prefix" ||
      state === "root" ||
      longAt(word, end, [ROOT_INDEX, SUFFIX_INDEX]) ||
      prefixThenRoot(word, end);
    if (!anchored) {
      continue;
    }
    const cost =
      PART_COST +
      shortPenalty(form, false) -
      ROOT_LENGTH_BONUS * Math.min(form.length, 6);
    move(end, "root", cost, "root", matches);
    // A silent final e: scrib + e, as in "describe".
    if (word[end] === "e" && startsSuffix(word, end + 1)) {
      move(end + 1, "root", cost + SILENT_E_COST, "root", matches);
    }
  }
  if (position < word.length) {
    move(position + 1, "stem", UNKNOWN_COST, "unknown");
  }
}

function moveSuffixes({ word, position, state, move }: Context): void {
  for (const { form, matches } of candidates(SUFFIX_INDEX, word, position)) {
    const helper = isHelper(matches);
    // A short suffix must sit straight after a root or another suffix; after an
    // unexplained stem only a long one is believable. After a prefix, only a long one.
    const short = form.length < LONG_FORM && !helper;
    if (short && state !== "root" && state !== "suffix") {
      continue;
    }
    if (state === "prefix" && form.length < LONG_FORM) {
      continue;
    }
    move(
      position + form.length,
      "suffix",
      PART_COST + shortPenalty(form, helper) - SUFFIX_BONUS,
      "suffix",
      matches
    );
  }
}

/** A suffix of three or more letters (an English ending such as -s does not count). */
function startsKnownSuffix(word: string, position: number): boolean {
  return candidates(SUFFIX_INDEX, word, position).some(
    (item) => item.form.length >= 3 && !isHelper(item.matches)
  );
}

function isJoiningVowel(word: string, position: number): boolean {
  const letter = word[position];
  return (
    (letter === "o" || letter === "i") &&
    (startsRoot(word, position + 1) || startsKnownSuffix(word, position + 1))
  );
}

function extend(
  table: Table,
  word: string,
  position: number,
  state: State
): void {
  const here = table.get(key(position, state));
  if (!here) {
    return;
  }
  const move: Mover = (end, next, cost, kind, matches = []) =>
    relax(table, end, next, {
      from: position,
      fromState: state,
      cost: here.cost + cost,
      part: { start: position, end, kind, matches },
    });
  const context: Context = { word, position, state, move };
  if (state !== "suffix") {
    movePrefixes(context);
    moveRoots(context);
  }
  if (state !== "start") {
    moveSuffixes(context);
  }
  // A connecting vowel between two roots: bio + o + logy -> "biology".
  if (state === "root" && isJoiningVowel(word, position)) {
    move(position + 1, "stem", GLUE_COST, "glue");
  }
}

const STATES: readonly State[] = ["start", "prefix", "root", "stem", "suffix"];

function pickMatch(matches: FormMatch[], text: string): FormMatch {
  const bare = text.replace(RE_SILENT_E, "");
  return (
    matches.find((match) => match.form === text && !match.entry.helper) ??
    matches.find((match) => match.form === bare && !match.entry.helper) ??
    matches.find((match) => match.form === text || match.form === bare) ??
    matches[0]
  );
}

function toPart(word: string, piece: Piece): WordPart {
  const { start, end, kind, matches } = piece;
  const text = word.slice(start, end);
  if (matches.length === 0) {
    return { text, kind, form: null, entry: null, alternatives: [] };
  }
  const best = pickMatch(matches, text);
  return {
    text,
    kind,
    form: best.form,
    entry: best.entry,
    alternatives: matches
      .filter((match) => match.form === best.form)
      .map((match) => match.entry)
      .filter((entry) => entry.id !== best.entry.id),
  };
}

/** Merge runs of unexplained letters into one part, so "xyz" is one "?" chip. */
function mergeUnknown(parts: WordPart[]): WordPart[] {
  const merged: WordPart[] = [];
  for (const part of parts) {
    const last = merged.at(-1);
    if (part.kind === "unknown" && last?.kind === "unknown") {
      last.text += part.text;
    } else {
      merged.push({ ...part });
    }
  }
  return merged;
}

function cheapestEnd(
  table: Table,
  length: number
): { step: Step; state: State } | null {
  let best: { step: Step; state: State } | null = null;
  for (const state of STATES) {
    const step = table.get(key(length, state));
    if (step && state !== "start" && (!best || step.cost < best.step.cost)) {
      best = { step, state };
    }
  }
  return best;
}

function trace(
  table: Table,
  word: string,
  end: { step: Step; state: State }
): WordPart[] {
  const parts: WordPart[] = [];
  let step: Step | undefined = end.step;
  while (step?.part) {
    parts.unshift(toPart(word, step.part));
    step = table.get(key(step.from, step.fromState));
  }
  return mergeUnknown(parts);
}

/** Is the reading believable: a real root or prefix, and most of the word explained? */
function believable(parts: readonly WordPart[], length: number): boolean {
  const known = parts
    .filter((part) => part.kind !== "unknown")
    .reduce((sum, part) => sum + part.text.length, 0);
  const anchor = parts.some(
    (part) =>
      part.entry !== null &&
      !part.entry.helper &&
      (part.kind === "root" || part.kind === "prefix") &&
      part.text.length >= 3
  );
  return anchor && known >= MIN_KNOWN && known / length >= MIN_COVERAGE;
}

/**
 * Explain a word as Greek and Latin parts. `none` when too little of it can be
 * explained to say anything useful, `lookalike` for the everyday words that only seem
 * to contain a part, `invalid` when the input is not one word of 4–40 letters.
 */
export function analyzeWord(input: string): WordAnalysis {
  const word = input.trim().toLowerCase().replace(RE_NON_LETTER, "");
  if (word.length < MIN_WORD || word.length > MAX_WORD) {
    return { status: "invalid" };
  }
  if (LOOKALIKES.has(word)) {
    return { status: "lookalike", word };
  }
  const table: Table = new Map();
  table.set(key(0, "start"), {
    from: 0,
    fromState: "start",
    cost: 0,
    part: null,
  });
  for (let position = 0; position <= word.length; position += 1) {
    for (const state of STATES) {
      extend(table, word, position, state);
    }
  }
  const end = cheapestEnd(table, word.length);
  if (!end) {
    return { status: "none", word };
  }
  const parts = trace(table, word, end);
  if (!believable(parts, word.length)) {
    return { status: "none", word };
  }
  const known = parts
    .filter((part) => part.kind !== "unknown")
    .reduce((sum, part) => sum + part.text.length, 0);
  return {
    status: "parts",
    breakdown: { word, parts, coverage: known / word.length },
  };
}
